import React from 'react';
import Layout from '../components/Layout';

const LOGO = "/images/smart-device-copy.png";

const values = [
  {
    icon: '📖',
    title: 'Accurate & Practical Guidance',
    desc: 'We create clear, tested, and regularly updated instructions for common device setup and troubleshooting issues across major brands and platforms.',
  },
  {
    icon: '🔒',
    title: 'Independent & Transparent',
    desc: 'We are not controlled by any manufacturer or retailer. Our recommendations and guides are created independently to prioritize user needs.',
  },
  {
    icon: '🆓',
    title: 'Free Help for Consumers',
    desc: 'Our educational guides and troubleshooting articles remain free for everyday users who need quick and reliable support.',
  },
  {
    icon: '💼',
    title: 'Professional Support for Businesses',
    desc: 'We offer paid B2B services for organizations that require dedicated technical assistance, device management support, onboarding help, and operational troubleshooting.',
  },
  {
    icon: '💬',
    title: 'Customer-Focused Approach',
    desc: 'We value user feedback and continuously improve our content and services based on real customer questions and device issues.',
  },
];

const team = [
  {
    name: 'Editorial Team',
    role: 'Content & Research',
    initials: 'ET',
    bio: 'Our writers and researchers test troubleshooting methods on real devices before publishing guides to ensure instructions are accurate and easy to follow.',
  },
  {
    name: 'Technical Review',
    role: 'Accuracy & Updates',
    initials: 'TR',
    bio: 'Technical reviewers verify setup procedures, compatibility details, and troubleshooting steps while keeping content updated with software and firmware changes.',
  },
  {
    name: 'Support Team',
    role: 'Consumer & Business Assistance',
    initials: 'ST',
    bio: 'Our support team monitors reader questions, identifies recurring device problems, and helps improve both our free resources and business support services.',
  },
];

const covers = [
  {
    title: 'Printer Setup',
    desc: 'Wired USB, wireless Wi-Fi, Bluetooth, and network printer configuration for Windows and Mac devices.',
  },
  {
    title: 'Printer Troubleshooting',
    desc: 'Fixing offline printers, connection errors, driver issues, print queue problems, and wireless printing failures.',
  },
  {
    title: 'Scanner Setup & Scanning Issues',
    desc: 'Step-by-step guidance for document scanning, driver installation, and resolving common scanner connectivity issues.',
  },
  {
    title: 'Smart Home Devices',
    desc: 'General setup and troubleshooting help for smart cameras, voice assistants, Wi-Fi extenders, and connected home devices.',
  },
  {
    title: 'Business Device Support',
    desc: 'Paid support solutions for offices, teams, and organizations needing help with device deployment, maintenance, troubleshooting workflows, and user assistance.',
  },
];

export default function AboutUs() {
  return (
    <Layout>
      <div className="page-banner">
        <h1>About Us</h1>
      </div>

      <div className="main-content">

        {/* Intro section */}
        <div className="about-intro">
          <div className="about-intro-text">
            <h2>Who We Are</h2>
            <p>
              Smart Device Assistant is an independent informational platform that helps individuals and
              businesses set up, configure, and troubleshoot printers, scanners, Wi-Fi extenders, and
              smart home devices.
            </p>
            <p>
              We started because device problems are frustrating — and official manuals are often difficult
              to understand, outdated, or too technical for everyday users. Our mission is to provide
              simple, practical, and reliable solutions that make technology easier for everyone.
            </p>
            <p>
              For everyday consumers (B2C), we publish free step-by-step guides and troubleshooting
              resources that anyone can follow. For businesses (B2B), we also provide professional paid
              support services designed to help organizations manage device setup, deployment,
              troubleshooting, and technical assistance more efficiently.
            </p>
            <p>
              We are an independent resource and are not owned by or affiliated with any device manufacturer.
            </p>
          </div>
          <div className="about-intro-logo">
            <img src={LOGO} alt="Smart Device Assistant" />
          </div>
        </div>

        {/* Mission statement */}
        <div className="about-mission">
          <h2>Our Mission</h2>
          <p>
            To provide accurate, unbiased, and easy-to-follow device support resources for consumers
            while delivering reliable technical assistance and managed support solutions for businesses.
          </p>
        </div>

        {/* Values grid */}
        <h2 style={{ marginBottom: '1.25rem' }}>What We Stand For</h2>
        <div className="about-values-grid">
          {values.map(v => (
            <div className="about-value-card" key={v.title}>
              <span className="about-value-icon">{v.icon}</span>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </div>
          ))}
        </div>

        {/* What we cover */}
        <div className="about-covers">
          <h2>What We Cover</h2>
          <p>We focus on solving the real-world problems users search for every day — not just repeating manufacturer documentation.</p>
          <ul>
            {covers.map(c => (
              <li key={c.title}><strong>{c.title}</strong> — {c.desc}</li>
            ))}
          </ul>
        </div>

        {/* Team section */}
        <h2 style={{ marginBottom: '1.25rem' }}>The People Behind the Site</h2>
        <div className="about-team-grid">
          {team.map(member => (
            <div className="about-team-card" key={member.name}>
              <div className="about-team-avatar">{member.initials}</div>
              <h3>{member.name}</h3>
              <span className="about-team-role">{member.role}</span>
              <p>{member.bio}</p>
            </div>
          ))}
        </div>

        {/* Disclaimer notice */}
        <div className="disclaimer-box" style={{ marginTop: '2.5rem' }}>
          <h3>A Note on Independence</h3>
          <p>
            Smart Device Assistant is an independent platform and is not affiliated with HP, Canon,
            Epson, Brother, Amazon, Arlo, or any other hardware or software manufacturer mentioned on
            this website. All product names, trademarks, and logos belong to their respective owners
            and are used strictly for identification and informational purposes.
          </p>
          <p>
            Our free guides are created independently for educational purposes. In addition, we may
            provide paid B2B technical assistance and support services for organizations requiring
            professional device support. However, we do not represent or act on behalf of any
            manufacturer unless explicitly stated.
          </p>
        </div>

      </div>
    </Layout>
  );
}
